import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  RowCustomAction,
  UserPermission
} from '@globaleris/ui.library'
import { Delete, Edit } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { PricePeriodTableModel } from '../../../api/Argentia/endpoints/pricePeriods/model/pricePeriods'
import { PricePeriodValueGroupedModel } from '../../../api/Argentia/endpoints/pricing/model/values'
import { EnumRedondeo } from '../../../api/Argentia/enums'
import { getCustomerDomain } from '../../../redux'
import { StandardCard } from '../standardCard/standardCard'

interface PricePeriodCardProps {
  show?: boolean
  isFetchingPricePeriods?: boolean
  pricePeriodData: PricePeriodTableModel | undefined
  onAddPricePeriod: () => void
  onEditPricePeriod: (selectedRow: PricePeriodValueGroupedModel) => void
  onDeletePricePeriod?: (selectedRow: PricePeriodValueGroupedModel) => void
  raised?: boolean
  rounding?: EnumRedondeo
}

export const PricePeriodCard: React.FC<PricePeriodCardProps> = ({
  show = false,
  isFetchingPricePeriods = false,
  pricePeriodData,
  onAddPricePeriod,
  onEditPricePeriod,
  onDeletePricePeriod,
  raised = false,
  rounding = EnumRedondeo.SeisDecimales,
  ...props
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'pricePeriodCard'])

  // #region Handlers Starts

  // ------------------------ DataTable Handlers ------------------------
  const handleRowAdd = async (): Promise<boolean> => {
    onAddPricePeriod()
    return true
  }

  const handleRowEdit = (selectedRow: PricePeriodValueGroupedModel) => {
    onEditPricePeriod(selectedRow)
  }

  const handleRowDelete = async (selectedRow: PricePeriodValueGroupedModel): Promise<void> => {
    onDeletePricePeriod && onDeletePricePeriod(selectedRow)
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const getCustomRowActions = () => {
    const defaultCustomActions: RowCustomAction[] = [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('pricePeriodCard:dataTable.tooltips.editPricePeriod'),
        onClick: handleRowEdit,
        userPermission: UserPermission.EDIT
      }
    ]

    let customActions: RowCustomAction[] = [...defaultCustomActions]

    onDeletePricePeriod &&
      customActions.push({
        icon: <Delete fontSize="small" />,
        tooltip: t('pricePeriodCard:dataTable.tooltips.deletePricePeriod'),
        onClick: handleRowDelete,
        userPermission: UserPermission.EDIT
      })

    return customActions
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const pricePeriodDataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    elevation: 0,
    canEdit: false,
    canDelete: false,
    canInsert: true,
    customInsertEditModal: true,
    pagination: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('pricePeriodCard:dataTable.analytics')
    },
    onRowInsert: handleRowAdd,
    rowCustomActions: getCustomRowActions()
  }

  const pricePeriodDataTableColumns: DataTableColumn[] = [
    {
      name: 'startDate',
      label: t('pricePeriodCard:dataTable.columns.startDate'),
      type: DataType.DATE,
      columnWidth: 12
    },
    {
      name: 'endDate',
      label: t('pricePeriodCard:dataTable.columns.endDate'),
      type: DataType.DATE,
      columnWidth: 12
    },
    {
      name: 'period1.value',
      label: t('pricePeriodCard:dataTable.columns.period1'),
      type: DataType.NUMBER,
      customDecimalScale: rounding
    },
    {
      name: 'period2.value',
      label: t('pricePeriodCard:dataTable.columns.period2'),
      type: DataType.NUMBER,
      customDecimalScale: rounding
    },
    {
      name: 'period3.value',
      label: t('pricePeriodCard:dataTable.columns.period3'),
      type: DataType.NUMBER,
      customDecimalScale: rounding
    },
    {
      name: 'period4.value',
      label: t('pricePeriodCard:dataTable.columns.period4'),
      type: DataType.NUMBER,
      customDecimalScale: rounding
    },
    {
      name: 'period5.value',
      label: t('pricePeriodCard:dataTable.columns.period5'),
      type: DataType.NUMBER,
      customDecimalScale: rounding
    },
    {
      name: 'period6.value',
      label: t('pricePeriodCard:dataTable.columns.period6'),
      type: DataType.NUMBER,
      customDecimalScale: rounding
    }
  ]

  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <>
      {show && (
        <StandardCard
          raised={raised}
          title={t('pricePeriodCard:title')}
          isLoading={isFetchingPricePeriods}
          style={{ margin: '16px 0 0 0', boxShadow: '1' }}
          {...props}
        >
          <DataTable
            data={pricePeriodData?.groupedValues ?? []}
            columns={pricePeriodDataTableColumns}
            options={pricePeriodDataTableOptions}
          />
        </StandardCard>
      )}
    </>
  )
}
